<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex items-center justify-between space-x-2">
            <h1 class="text-2xl font-semibold text-gray-700">Activations</h1>
            <div class="flex items-center space-x-2">
                <USelectMenu
                    v-if="scopedPartners.length > 1"
                    v-model="partnerId"
                    :options="[{ id: ALL_PARTNERS, name: 'All Partners' }, ...scopedPartners]"
                    value-attribute="id"
                    option-attribute="name"
                    class="w-52"
                    leading-icon="i-ph-handshake-duotone"
                />
                <UInput
                    v-model="search"
                    type="text"
                    placeholder="Search..."
                    icon="i-ph-magnifying-glass-duotone"
                />
                <UButton v-if="canBulkActivate" @click="overlays.bulkActivate = true" color="white" icon="i-ph-upload-simple-bold">
                    Bulk Activation
                </UButton>
                <UButton v-if="quickActivationPartners.length" @click="overlays.quickActivate = true" color="black" icon="i-ph-plus-bold">
                    Quick Activation
                </UButton>
            </div>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="filteredActivations"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-shield-plus-duotone', label: 'No activations.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', td: { padding: 'py-3' } }"
                class="h-full"
            >
                <template #phone-data="{ row }">
                    <PhoneLabel v-if="row.phone" :value="`+${row.phone}`" />
                    <span v-else class="text-gray-300">&mdash;</span>
                </template>
                <template #partner-data="{ row }">
                    <span>{{ partnerName(row.batch?.partner_id) }}</span>
                </template>
                <template #status-data="{ row }">
                    <UTooltip :text="row.error_message || ''" :prevent="!row.error_message">
                        <BadgeActivationStatus :status="row.status" />
                    </UTooltip>
                </template>
                <template #expires_at-data="{ row }">
                    <TableDateCell v-if="row.subscription?.expires_at" :date="row.subscription.expires_at" />
                    <span v-else class="text-gray-300">&mdash;</span>
                </template>
                <template #created_at-data="{ row }">
                    <TableDateCell :date="row.created_at" />
                </template>
                <template #actions-data="{ row }">
                    <UDropdown v-if="actions(row)[0].length" :items="actions(row)">
                        <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                    </UDropdown>
                </template>
            </UTable>
        </div>

        <OverlayCredentialQuickActivation
            v-model:open="overlays.quickActivate"
            :partners="quickActivationPartners"
            @reload="fetchData"
        />
        <OverlayCredentialBulkActivation
            v-model:open="overlays.bulkActivate"
            :preset-partner="overview?.partner"
            @update:open="!$event && fetchData()"
        />
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.partner] })
useHead({ title: 'Activations' })

interface Activation {
    id: string
    full_name: string
    phone: string | null
    status: string
    error_message: string | null
    created_at: string
    subscription?: { expires_at: string } | null
    batch?: { partner_id: string } | null
}

const route = useRoute()
const toast = useToast()
const client = useSupabaseClient()
const { overview, scopedPartners, quickActivationPartners, canBulkActivate, load, partnerName } = usePartnerOverview()

const loading = ref<boolean>(false)
const search = ref<string>('')
const ALL_PARTNERS = 'all'
const partnerId = ref<string>((route.query.partner_id as string | undefined) || ALL_PARTNERS)
const activations = ref<Activation[]>([])

const overlays = ref({
    quickActivate: false,
    bulkActivate: false,
})

const columns = computed(() => [
    { key: 'full_name', label: 'Name' },
    { key: 'phone', label: 'Phone' },
    ...(scopedPartners.value.length > 1 ? [{ key: 'partner', label: 'Partner' }] : []),
    { key: 'status', label: 'Status' },
    { key: 'expires_at', label: 'Expires' },
    { key: 'created_at', label: 'Activated' },
    { key: 'actions', label: '' },
])

const filteredActivations = computed(() => {
    const q = search.value.trim().toLowerCase()
    if (!q) return activations.value
    return activations.value.filter(a => a.full_name.toLowerCase().includes(q) || a.phone?.includes(q.replace(/\D/g, '') || q))
})

watch(partnerId, (id) => {
    navigateTo({ query: id !== ALL_PARTNERS ? { partner_id: id } : {} }, { replace: true })
    fetchData()
})

onMounted(async () => {
    await load()
    await fetchData()
})

async function fetchData() {
    loading.value = true

    try {
        activations.value = await $fetch<Activation[]>('/api/partner/activations', {
            query: partnerId.value !== ALL_PARTNERS ? { partner_id: partnerId.value } : {},
        })
    } catch (error: any) {
        toast.add({ title: 'Could not load activations', description: error.data?.message, color: 'red' })
    }

    loading.value = false
}

function actions(row: Activation) {
    const canResend = row.status === 'whatsapp_failed' && row.batch?.partner_id === overview.value?.partner.id

    return [canResend ? [{
        label: 'Resend WhatsApp',
        icon: 'i-ph-whatsapp-logo-duotone',
        click: () => resend(row),
    }] : []]
}

async function resend(row: Activation) {
    const { error } = await client.functions.invoke('resend-whatsapp', {
        body: { recordId: row.id },
    })

    if (error) {
        toast.add({ title: 'Could not send WhatsApp message', color: 'red' })
        return
    }

    toast.add({ title: 'WhatsApp message sent', color: 'green' })
    await fetchData()
}
</script>
